<template>
  <div v-if="isOpen" class="fixed inset-0 isolate z-50 overflow-auto">
    <div class="bg-overlay-low fixed inset-0" data-testid="overlay" />
    <!-- We define the keydown handler on the div to catch all events -->
    <!-- emitted from child elements. We do not care about the div. -->
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
    <div
      ref="dialog"
      aria-labelledby="modalTitle"
      aria-modal="true"
      class="bg-surface-default-hi rounded-t-lg fixed bottom-0 left-0 max-h-full w-full overflow-auto md:rounded-b-lg md:bottom-auto md:left-1/2 md:top-1/2 md:w-640 md:-translate-x-1/2 md:-translate-y-1/2"
      role="dialog"
      tabindex="0"
      @keydown="focusTrap"
    >
      <section class="flex h-full max-h-screen flex-col justify-between">
        <div class="h-full px-24">
          <FirstStep
            :loading="isLoading"
            :zone="`${COOKIES_TRACKING_NAME.MODAL_STEP}1`"
            @accept="acceptAll"
            @params="showCookiesModal"
            @refuse="declineAll"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useRoute, useRuntimeConfig } from '#imports'
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue'

import { useIsE2ETestingEnvironment } from '@backmarket/nuxt-module-qa/useIsE2ETestingEnvironment'

import { useDataConsent } from '../../composables/useDataConsent'

import FirstStep from './FirstStep.vue'
import { COOKIES_TRACKING_NAME } from './constants'

const {
  tracking: { didomi },
} = useRuntimeConfig().public

const { showCookiesModal, acceptAll, declineAll, isCookiesModalDisplayed } =
  useDataConsent()

// Cookie modal must be hidden on some pages (legal pages) but displayed afterwards.
// Cookie modal must be hidden on e2e testing environment.
const route = useRoute()
const isE2ETestingEnvironment = useIsE2ETestingEnvironment()

const shouldHideModal = computed(() => {
  return isE2ETestingEnvironment || route.meta?.noCookieModal
})

const isLoading = ref(true)
const dialog = useTemplateRef<HTMLElement | null>('dialog')
const isOpen = computed(
  () =>
    !shouldHideModal.value &&
    didomi.isCustomModalEnabled &&
    isCookiesModalDisplayed.value,
)

function focusTrap(event: KeyboardEvent) {
  if (dialog.value) {
    const { key, shiftKey, target } = event

    if (key === 'Tab') {
      const focusableList = dialog.value.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )

      if (focusableList.length < 2) {
        event.preventDefault()

        return
      }

      const last = focusableList.length - 1

      if (shiftKey === false && target === focusableList[last]) {
        ;(focusableList[0] as HTMLElement).focus()

        event.preventDefault()
      } else if (shiftKey === true && target === focusableList[0]) {
        ;(focusableList[last] as HTMLElement).focus()

        event.preventDefault()
      }
    }
  }
}

watch(isOpen, (value) => {
  // When we close the modal we allow the user to scroll on the body again
  window.document.body.style.overflow = value ? 'hidden' : ''
  window.document.body.style.position = value ? 'relative' : ''
  window.document.body.style.height = value ? '100%' : ''
})

onMounted(() => {
  isLoading.value = false

  if (isOpen.value) {
    window.document.body.style.overflow = 'hidden'
    window.document.body.style.position = 'relative'
    window.document.body.style.height = '100%'
  }
})

onBeforeUnmount(() => {
  // When we close the modal we make sure that users can scroll on body again
  window.document.body.style.overflow = ''
  window.document.body.style.position = ''
  window.document.body.style.height = ''
})
</script>
